import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import chalk from "chalk";
import type { SmartNumberMapping } from "@/features/sub-families/types";
import { getProductScenario } from "@/features/sub-families/utils/power-supply-dimming-type.ts";
import Logger from "@/lib/logger.ts";
import { getProductPropertyByDescriptor } from "@/lib/product.ts";
import { productsSetCurrentProductReferenceAction } from "@/redux/slices/products-slice.ts";
import { SmartNumberDescriptor } from "@/types";
import {
    smartNumberChangeMappingAction,
    smartNumberChangeMultipleColoursAction,
    smartNumberChangeMultipleMappingAction,
    smartNumberOverrideMappingAction,
    smartNumberSetCalculatedResultAction,
    smartNumberSetDescriptionAction,
    smartNumberSetMappingAction,
} from "../slices/smart-number-slice.ts";
import type { AppDispatch, RootState } from "../store.ts";

const smartNumberMiddleware = createListenerMiddleware();

/**
 * Recalculates the smart-number whenever the internal mapping changes
 */
smartNumberMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    matcher: isAnyOf(
        smartNumberSetDescriptionAction,
        smartNumberSetMappingAction,
        smartNumberOverrideMappingAction,
        smartNumberChangeMappingAction,
        smartNumberChangeMultipleMappingAction,
        smartNumberChangeMultipleColoursAction,
    ),
    effect: (_, api) => {
        const state = api.getState();
        const { mapping, description } = state.smartNumber;

        if (!mapping || description.length === 0) return;

        const smartNumber = description.reduce<string>((acc, descriptor) => {
            const value = mapping[descriptor.CharacteristicName];
            if (value) acc += value;

            return acc;
        }, "");

        api.dispatch(smartNumberSetCalculatedResultAction(smartNumber));
    },
});

/**
 * Rebuilds the mapping upon a complete product reference change
 */
smartNumberMiddleware.startListening.withTypes<RootState, AppDispatch>()({
    actionCreator: productsSetCurrentProductReferenceAction,
    effect: (action, api) => {
        const productReference = action.payload;
        if (!productReference) {
            Logger.warn("No product reference was given, cannot recalculate smart number!");
            return;
        }

        const state = api.getState();
        const { items } = state.products;
        const { description, mapping } = state.smartNumber;

        // Should not happen at this point in time
        if (!description || !mapping) return;

        const product = items[productReference];
        if (!product) {
            Logger.warn("Could not find a product by reference", chalk.cyan(productReference));
            return;
        }

        const productScenario = getProductScenario(product);

        const updatedMapping = description.reduce<SmartNumberMapping>(
            (acc, curr) => {
                const descriptor = curr.CharacteristicName;
                const propertyKey = getProductPropertyByDescriptor(descriptor);

                switch (propertyKey) {
                    // Properties of massCode (except for DimType since this has its own logic)
                    case "light_distributor":
                    case "ledchip":
                    case "dimension":
                    // Properties of Light Technical
                    case "cri":
                    case "cct":
                    case "beam_angle":
                        acc[descriptor] = product.properties[propertyKey].configCode;
                        break;

                    case "dimming_type":
                        if (productScenario === 1 || productScenario === 4) {
                            acc[SmartNumberDescriptor.DimType] = product.properties.dimming_type.configCode;
                        }

                        break;

                    default:
                        // Should power supply / dimming types / accessories be reset?
                        break;
                }

                return acc;
            },
            // Clear the readonly status from the mapping
            JSON.parse(JSON.stringify(mapping)) as SmartNumberMapping,
        );

        api.dispatch(smartNumberOverrideMappingAction(updatedMapping));
    },
});

export default smartNumberMiddleware.middleware;
